import constate from 'constate';
import { useReducer, useCallback } from 'react';
import createReducer from 'helpers/reducers-helper';
import repo from 'service/repository';
import merge from 'lodash/merge';
import assign from 'lodash/assign';
import get from 'lodash/get';
import take from 'lodash/take';
import reduce from 'lodash/reduce';
import { useAppContext, SHOW_APP_MESSAGE, UPDATE_USER_INFO } from 'use-app';
import { errorHandler } from 'helpers/error-handler';
import dayjs from 'dayjs';

export const SWITCH_LOADING = 'SWITCH_LOADING';
export const SET_NO_CREDIT_CARD_DIALOG = 'SET_NO_CREDIT_CARD_DIALOG';
export const SET_NO_LICENSE_PLATE_DIALOG = 'SET_NO_LICENSE_PLATE_DIALOG';

export const SELECT_CITY = 'SELECT_CITY';
export const SELECT_PLATE = 'SELECT_PLATE';
export const LIST_STREET_PARKING_BILLS = 'LIST_STREET_PARKING_BILLS';

export const PICK_PAYING_BILLS = 'PICK_PAYING_BILLS';
export const PICK_ALL_PAYING_BILLS = 'PICK_ALL_PAYING_BILLS';
export const PAY_STATE = 'PAY_STATE';
export const RESET_PAY_STATE = 'RESET_PAY_STATE';
export const SHOW_TERMS_OF_TAIPEI_SERVICE_DIALOG = 'show_term_of_taipei_service_dialog';
export const HIDE_TERMS_OF_TAIPEI_SERVICE_DIALOG = 'hide_term_of_taipei_service_dialog';
export const UPDATE_AUTOPAY_BOARD = 'update_autopay_board';
export const UPDATE_SEARCH_BILL_BOARD = 'search_bill_board';
export const UPDATE_PAY_IN_FULL = 'update_pay_in_full';

const useOnStreetParking = () => {
  const MAX_SELECTED_BILL_COUNT = 10;
  const { state: appState, dispatch: appDispatch } = useAppContext();

  const initState = {
    loading: false,

    // setNoCreditCardNoLicensePlateDialog: false,
    setNoLicensePlateDialog: false,
    setNoCreditCardDialog: false,

    selectedCityName: '',
    streetParkingCitiesButtonDisable: true,

    selectedPlateNumber: '',

    streetParkingBillsButtonDisable: true,
    streetParkingBills: [],
    currentSelectBillsCode: [],
    totalAmount: 0,
    selectAllChecked: false,
    payState: '',
    payMsg: '',
    payStateShowing: false,
    showTermOfTaipeiServiceDialog: false,
    orderUrl: '',
    autopayBoard: null,
    searchBillBoard: null,
    payInFull: false,
  };

  const switchLoading = (state, payload) => merge({}, state, { loading: payload });
  const setNoCreditCardDialog = (state, payload) => merge({}, state, { setNoCreditCardDialog: payload });
  const setNoLicensePlateDialog = (state, payload) => merge({}, state, { setNoLicensePlateDialog: payload });
  const selectCity = (state, payload) =>
    merge({}, state, { selectedCityName: payload, streetParkingCitiesButtonDisable: false });
  const selectPlate = (state, payload) =>
    merge({}, state, { selectedPlateNumber: payload, streetParkingPlatesButtonDisable: false });
  const listStreetParkingBills = (state, payload) => {
    let autoSelectedCount = MAX_SELECTED_BILL_COUNT;
    if (state.payInFull) {
      autoSelectedCount = payload.length;
    }
    const defaultSelectedBills = take(payload, autoSelectedCount);
    return assign({}, state, {
      streetParkingBills: payload,
      currentSelectBillsCode: defaultSelectedBills.map(x => x.code),
      totalAmount: amountSum(defaultSelectedBills),
      streetParkingBillsButtonDisable: false,
      selectAllChecked: isMaxBillsSelected(payload.length, defaultSelectedBills.length),
    });
  };
  const pickPayingBills = (state, payload) => {
    const { currentSelectBillsCode } = state;

    const isBillSelected = currentSelectBillsCode.indexOf(payload) !== -1;

    if (isBillSelected) {
      // remove bill from selected bill list
      const newSelectedBills = state.streetParkingBills.filter(
        x => currentSelectBillsCode.indexOf(x.code) !== -1 && x.code !== payload
      );
      return assign({}, state, {
        currentSelectBillsCode: newSelectedBills.map(x => x.code),
        totalAmount: amountSum(newSelectedBills),
        selectAllChecked: isMaxBillsSelected(state.streetParkingBills.length, newSelectedBills.length),
        streetParkingBillsButtonDisable: !buttonEnable(newSelectedBills),
      });
    } else {
      if (isMaxBillsSelected(state.streetParkingBills.length, currentSelectBillsCode.length)) {
        // show max selected limitation message
        appDispatch({
          type: SHOW_APP_MESSAGE,
          payload: {
            type: 'failure',
            message: '很抱歉，為確保繳費流程順暢，一次最多繳費單數為 10 筆',
          },
        });
        return state;
      } else {
        // add bill into selected bill list
        const newSelectedBills = state.streetParkingBills.filter(
          x => currentSelectBillsCode.indexOf(x.code) !== -1 || x.code === payload
        );
        return assign({}, state, {
          currentSelectBillsCode: newSelectedBills.map(x => x.code),
          totalAmount: amountSum(newSelectedBills),
          selectAllChecked: isMaxBillsSelected(state.streetParkingBills.length, newSelectedBills.length),
          streetParkingBillsButtonDisable: !buttonEnable(newSelectedBills),
        });
      }
    }
  };

  const pickAllPayingBills = state => {
    if (isMaxBillsSelected(state.streetParkingBills.length, state.currentSelectBillsCode.length)) {
      return assign({}, state, {
        currentSelectBillsCode: [],
        selectAllChecked: false,
        totalAmount: 0,
        streetParkingBillsButtonDisable: true,
      });
    } else {
      const selectedCount = state.currentSelectBillsCode.length;
      const selectedBills = state.streetParkingBills.filter(x => state.currentSelectBillsCode.indexOf(x.code) !== -1);
      const unSelectedBills = state.streetParkingBills.filter(x => state.currentSelectBillsCode.indexOf(x.code) === -1);
      const needSelectBills = take(unSelectedBills, MAX_SELECTED_BILL_COUNT - selectedCount);

      const newSelectedBills = selectedBills.concat(needSelectBills);
      return merge({}, state, {
        currentSelectBillsCode: newSelectedBills.map(x => x.code),
        selectAllChecked: true,
        totalAmount: amountSum(newSelectedBills),
        streetParkingBillsButtonDisable: false,
      });
    }
  };

  const amountSum = payingBills => {
    let reducer = (acc, cur) => acc + cur;
    let payingBillsAmount = payingBills.map(bill => Math.round(bill.amount));
    const result = reduce(payingBillsAmount, reducer);
    return result ? result : 0;
  };

  const isMaxBillsSelected = (totalBillCount, selectedBillCount) =>
    totalBillCount === selectedBillCount || selectedBillCount === MAX_SELECTED_BILL_COUNT;
  const buttonEnable = bills => bills.length !== 0;

  const payState = (state, payload) =>
    merge({}, state, {
      payStateShowing: true,
      payState: payload.payState,
      payMsg: payload.payMsg,
      orderUrl: payload.orderUrl,
    });
  const resetPayState = state => assign({}, state, { payStateShowing: false, payState: '', payMsg: '' });
  const showTermsOfTaipeiServiceDialog = state => merge({}, state, { showTermOfTaipeiServiceDialog: true });
  const hideTermsOfTaipeiServiceDialog = state => merge({}, state, { showTermOfTaipeiServiceDialog: false });
  const updateAutopayBoard = (state, payload) => {
    const enableOnStreetParkingAutoPayUpdatedAt = get(appState, 'me.enableOnStreetParkingAutoPayUpdatedAt', null);
    let needShowBoard = true;
    if (enableOnStreetParkingAutoPayUpdatedAt) {
      const targetDate = dayjs().year(2020).month(1).date(14).endOf('date');
      needShowBoard = dayjs(enableOnStreetParkingAutoPayUpdatedAt).isAfter(targetDate);
    }

    if (needShowBoard && payload.length > 0) {
      return assign({}, state, { autopayBoard: payload[0] });
    } else {
      return state;
    }
  };

  const updateSearchBillBoard = (state, payload) => {
    if (payload.length > 0) {
      return assign({}, state, { searchBillBoard: payload[0] });
    } else {
      return state;
    }
  };

  const updatePayInFull = (state, payload) => {
    return assign({}, state, { payInFull: payload });
  };

  const reducer = createReducer(
    {
      [SWITCH_LOADING]: switchLoading,
      // [SET_NO_CREDIT_CARD_NO_LICENSE_PLATE_DIALOG]: setNoCreditCardNoLicensePlateDialog,
      [SET_NO_CREDIT_CARD_DIALOG]: setNoCreditCardDialog,
      [SET_NO_LICENSE_PLATE_DIALOG]: setNoLicensePlateDialog,
      [SELECT_CITY]: selectCity,
      [SELECT_PLATE]: selectPlate,
      [LIST_STREET_PARKING_BILLS]: listStreetParkingBills,
      [PICK_PAYING_BILLS]: pickPayingBills,
      [PICK_ALL_PAYING_BILLS]: pickAllPayingBills,
      [PAY_STATE]: payState,
      [RESET_PAY_STATE]: resetPayState,
      [SHOW_TERMS_OF_TAIPEI_SERVICE_DIALOG]: showTermsOfTaipeiServiceDialog,
      [HIDE_TERMS_OF_TAIPEI_SERVICE_DIALOG]: hideTermsOfTaipeiServiceDialog,
      [UPDATE_AUTOPAY_BOARD]: updateAutopayBoard,
      [UPDATE_SEARCH_BILL_BOARD]: updateSearchBillBoard,
      [UPDATE_PAY_IN_FULL]: updatePayInFull,
    },
    initState,
    true
  );

  const [state, dispatch] = useReducer(reducer, initState);

  const switchStreetParkingAutoPay = () => {
    const { enableOnStreetParkingAutoPay } = appState.me;
    dispatch({
      type: SWITCH_LOADING,
      payload: true,
    });
    repo
      .updateUserInfo(undefined, undefined, undefined, !enableOnStreetParkingAutoPay)
      .then(value => {
        appDispatch({
          type: UPDATE_USER_INFO,
          payload: value,
        });
        if (!enableOnStreetParkingAutoPay) {
          dispatch({ type: SHOW_TERMS_OF_TAIPEI_SERVICE_DIALOG });
        }
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() =>
        dispatch({
          type: SWITCH_LOADING,
          payload: false,
        })
      );
  };

  const getStreetParkingBills = useCallback(
    (cityId, plate) => {
      const cities = get(appState, 'bootstraps.searchStreetParkingBillsCities', []);
      const payInFull = cities.filter(x => x.id === cityId).some(x => x.payInFull);
      dispatch({
        type: UPDATE_PAY_IN_FULL,
        payload: payInFull,
      });
      dispatch({
        type: SWITCH_LOADING,
        payload: true,
      });
      Promise.all([repo.fetchStreetParkingBills(cityId, plate), repo.fetchSearchBillBoard()])
        .then(value => {
          const bills = value[0];
          const boards = value[1];
          dispatch({
            type: LIST_STREET_PARKING_BILLS,
            payload: bills,
          });
          dispatch({
            type: UPDATE_SEARCH_BILL_BOARD,
            payload: boards.filter(x => x.attributes.cityId === cityId),
          });
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          dispatch({
            type: SWITCH_LOADING,
            payload: false,
          });
        });
    },
    [appState, dispatch]
  );

  const payStreetParkingBills = (cityId, plate) => {
    dispatch({
      type: SWITCH_LOADING,
      payload: true,
    });
    repo
      .payStreetParkingBills(cityId, state.currentSelectBillsCode, plate)
      .then(value => {
        dispatch({
          type: PAY_STATE,
          payload: {
            payState: 'success',
            payMsg: '繳費成功',
            orderUrl: value.url,
          },
        });
      })
      .catch(error => {
        dispatch({
          type: PAY_STATE,
          payload: {
            payState: 'failure',
            payMsg: errorHandler(error),
          },
        });
      })
      .finally(() => {
        dispatch({
          type: SWITCH_LOADING,
          payload: false,
        });
      });
  };

  const findCityNameById = id => {
    const cities = get(appState, 'bootstraps.searchStreetParkingBillsCities', []);
    return cities.find(x => x.id === id).name;
  };

  const fetchAutopayBoard = useCallback(() => {
    dispatch({
      type: SWITCH_LOADING,
      payload: true,
    });
    repo
      .fetchAutopayBoard()
      .then(value => {
        dispatch({
          type: UPDATE_AUTOPAY_BOARD,
          payload: value,
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        dispatch({
          type: SWITCH_LOADING,
          payload: false,
        });
      });
  }, [dispatch]);

  return {
    state,
    dispatch,
    appState,
    findCityNameById,
    switchStreetParkingAutoPay,
    getStreetParkingBills,
    payStreetParkingBills,
    fetchAutopayBoard,
  };
};

const [StreetParkingProvider, useStreetParkingContext] = constate(useOnStreetParking);

export { StreetParkingProvider, useStreetParkingContext };
